// src/components/solution_sections/UseCasesCustomizationSection.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Card } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import { INNER_CARD_ANIMATE_RESTING, INNER_CARD_HOVER } from '../../shared/animationConstants';

// Define the data shapes for this component
interface UseCase {
    icon?: string;
    title: string;
    description: string;
}

interface UseCasesData {
    sectionTitle?: string;
    icon?: string;
    introduction?: string;
    primaryUseCases: UseCase[];
    customization: {
        title: string;
        description: string;
    };
}

interface UseCasesCustomizationSectionProps {
  data: UseCasesData;
  id: string;
  headerBgColor?: string;
}

// --- THE FIX IS HERE ---
// Added the missing closing `}) =>` after the props destructuring
export const UseCasesCustomizationSection = React.forwardRef<HTMLDivElement, UseCasesCustomizationSectionProps>(({
  data,
  id,
  headerBgColor,
}, ref) => {
// --- END OF FIX ---
  const { t } = useTranslation('solution_showcase');

  if (!data) return null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.15, ease: 'easeOut' } },
  };

  return (
    <Card
      ref={ref}
      title={data.sectionTitle || t('useCases.defaultTitle')}
      icon={data.icon || 'wrench'}
      id={id}
      headerBgColor={headerBgColor}
    >
      <div className="space-y-4">
        {data.introduction && (
          <p className="text-lg text-center text-gray-600 max-w-3xl mx-auto">{data.introduction}</p>
        )}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 pt-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          
          {data.primaryUseCases && data.primaryUseCases.length > 0 && (
            <motion.div className="space-y-5" variants={itemVariants}>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800">{t('useCases.primaryUseCasesTitle')}</h3>
              {data.primaryUseCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.15, delay: index * 0.1 }}
                  animate={INNER_CARD_ANIMATE_RESTING}
                  whileHover={INNER_CARD_HOVER}
                  className="flex items-start gap-4 p-4 bg-white rounded-lg border-2 border-black"
                >
                  <div className="flex-shrink-0 text-teal-600 bg-teal-100 p-3 rounded-lg border-2 border-black mt-1 shadow-sm">
                    {renderIcon(useCase.icon || 'target', { size: 24 })}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{useCase.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{useCase.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {data.customization && (
            <motion.div variants={itemVariants}>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-5">{data.customization.title}</h3>
              <motion.div
                initial={{ boxShadow: 'none' }}
                whileHover={{ y: -4, x: -4, boxShadow: '8px 8px 0px 0px #f97316' }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="prose prose-base max-w-none text-gray-800 leading-relaxed bg-gradient-to-br from-amber-50 to-orange-100 p-6 rounded-xl border-2 border-black"
              >
                <p>{data.customization.description}</p>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </Card>
  );
});

